<template>
  <div :class="fieldClass">
    <el-cascader
      v-if="isCascaderType"
      :value="value"
      @input="handleInput"
      @change="handleChange"
      @visible-change="handleVisibleChange"
      :options="availableOptions"
      :placeholder="translatePlaceholder(field.placeholder)"
      clearable
      filterable
      :disabled="field.isDisabled"
      :size="fieldSize"
      style="width: 100%"
      :props="{ expandTrigger: 'hover', checkStrictly: true, multiple: true }"
    ></el-cascader>
    
    <!--  SELECT : Pour tous les autres types -->
    <el-select
      v-else
      :value="value"
      @input="handleInput"
      @change="handleChange"
      @visible-change="handleVisibleChange"
      :placeholder="translatePlaceholder(field.placeholder)"
      :clearable="true"
      :filterable="true"
      :multiple="isMultiple"
      :disabled="field.isDisabled"
      :size="fieldSize"
      :loading="loading"
      :remote="getProperty('remote', false)"
      :remote-method="getProperty('remote') ? handleRemoteSearch : null"
      :reserve-keyword="getProperty('reserveKeyword', false)"
      :collapse-tags="getProperty('collapseTags', false)"
      :multiple-limit="getProperty('multipleLimit', 0)"
      style="width: 100%"
      :popper-append-to-body="true"
      popper-class="api-search-select-dropdown"
    >
      <el-option
        v-for="(option, index) in availableOptions"
        :key="option.value !== undefined ? option.value : (option.code || index)"
        :label="translateLabel(option.label || option.libelle || option.name)"
        :value="option[valueField] !== undefined ? option[valueField] : (option.value !== undefined ? option.value : option.code)"
        :disabled="option.disabled"
      >
        <span v-if="getProperty('showDescription') && option.description" class="option-with-description">
          <strong>{{ translateLabel(option.label || option.libelle || option.name) }}</strong>
          <br>
          <small class="text-muted">{{ option.description }}</small>
        </span>
      </el-option>
      
      <!-- Slot pour options personnalisées -->
      <template v-if="$slots.options">
        <slot name="options" :options="availableOptions" :loading="loading"></slot>
      </template>
    </el-select>
    
    <!-- Aide contextuelle -->
    <div v-if="field.helpText" class="field-help-text">
      <small class="text-muted">{{ $t(field.helpText) }}</small>
    </div>
    
    <!-- Indicateur de chargement des données -->
    <div v-if="dataLoading" class="field-loading">
      <small class="text-info">
        <i class="el-icon-loading"></i>
        {{ $t('components.searchField.loadingData') }}
      </small>
    </div>
  </div>
</template>

<script>
import { searchFormApi } from '@/services/searchForm/api'
import placeholderTranslation from '@/mixins/placeholderTranslation'

export default {
  name: 'GenericFieldSelect',
  mixins: [placeholderTranslation],
  props: {
    field: {
      type: Object,
      required: true
    },
    value: {
      type: [String, Number, Array],
      default: null
    },
    businessDomain: {
      type: String,
      required: true
    },
    userContext: {
      type: Object,
      default: () => ({})
    },
    formFilters: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      options: [],
      loading: false,
      dataLoading: false,
      remoteOptions: [],
      lastRemoteQuery: ''
    }
  },
  computed: {
    fieldClass() {
      return {
        'search-field': true,
        'search-field-select': true,
        'search-field-required': this.field.isRequired,
        'search-field-disabled': this.field.isDisabled,
        'search-field-multiple': this.isMultiple
      }
    },

    //  Déterminer quel champ utiliser comme valeur
    valueField() {
      return this.field.properties && this.field.properties.valueField || 'value'
    },

    //  CORRECTION CRITIQUE : Détection automatique du mode multiple
    isMultiple() {
      // 1. Si explicitement défini dans les properties
      if (this.field.properties && this.getProperty('multiple') !== undefined) {
        return this.getProperty('multiple', false)
      }
      
      // 2. Auto-détection basée sur le type de champ
      if (this.field.fieldType && this.field.fieldType.code) {
        const fieldType = this.field.fieldType.code.toLowerCase()
        return fieldType === 'multiselect'  //  Cascader n'est PAS un select multiple
      }
      
      // 3. Auto-détection basée sur la valeur (Array = multiple)
      if (Array.isArray(this.value)) {
        return true
      }
      
      return false
    },
    
    //  DÉTECTION CASCADER : Utilise el-cascader pour les types hiérarchiques  
    isCascaderType() {
      if (this.field.fieldType && this.field.fieldType.code) {
        const fieldType = this.field.fieldType.code.toLowerCase()
        return fieldType === 'cascader'
      }
      return false
    },
    
    availableOptions() {
      // Utilise les options remote si disponibles, sinon les options locales
      return this.getProperty('remote') && this.remoteOptions.length > 0 
        ? this.remoteOptions 
        : this.options
    },

    // Taille du champ - undefined = taille par défaut Element UI (comme l'original)
    fieldSize() {
      const size = this.getProperty('size')
      return size || undefined
    }
  },
  watch: {
    'field.dataSource': {
      handler() {
        //  Protection contre les boucles infinies
        if (this._loadingOptions) {
          return
        }
        this._loadingOptions = true
        this.$nextTick(() => {
          this._loadingOptions = false
          this.loadOptions()
        })
      },
      immediate: false
    },
    formFilters: {
      handler(newFilters, oldFilters) {
        const dependsOn = this.getProperty('dependsOn')
        if (dependsOn) {
          console.log(`[GenericFieldSelect ${this.field.name}] formFilters changed, dependsOn=${dependsOn}`, {
            newValue: newFilters[dependsOn],
            oldValue: oldFilters && oldFilters[dependsOn],
            hasChanged: newFilters[dependsOn] !== (oldFilters && oldFilters[dependsOn])
          })
          if (newFilters[dependsOn] !== (oldFilters && oldFilters[dependsOn])) {
            console.log(`[GenericFieldSelect ${this.field.name}] Loading dependent options for:`, newFilters[dependsOn])
            this.loadDependentOptions(newFilters[dependsOn])
          }
        }
      },
      deep: true
    }
  },
  async mounted() {
    console.log(`[GenericFieldSelect ${this.field.name}] mounted, formFilters:`, this.formFilters)
    console.log(`[GenericFieldSelect ${this.field.name}] field config:`, this.field)
    try {
      await this.loadOptions()
    } catch (error) {
      console.error(`[GenericFieldSelect ${this.field.name}] Error in mounted loadOptions:`, error)
    }
    
  },
  methods: {
    async loadOptions() {
      console.log(`[GenericFieldSelect ${this.field.name}] loadOptions START`)
      
      if (!this.field.dataSource) {
        console.log(`[GenericFieldSelect ${this.field.name}] No dataSource, returning`)
        return
      }

      console.log(`[GenericFieldSelect ${this.field.name}] Has dataSource:`, this.field.dataSource)

      const dependsOn = this.getProperty('dependsOn')
      console.log(`[GenericFieldSelect ${this.field.name}] dependsOn:`, dependsOn)
      
      if (dependsOn) {
        const dependencyValue = this.formFilters[dependsOn]
        console.log(`[GenericFieldSelect ${this.field.name}] loadOptions with dependency ${dependsOn}:`, dependencyValue)
        if (!dependencyValue) {
          console.log(`[GenericFieldSelect ${this.field.name}] No dependency value, clearing options`)
          this.options = []
          return
        }
        console.log(`[GenericFieldSelect ${this.field.name}] Loading dependent options at mount`)
        await this.loadDependentOptions(dependencyValue)
        return
      }

      this.dataLoading = true
      
      try {
        //  Si la source est de type 'store', récupérer depuis le Vuex store
        if (this.field.dataSource.type === 'store') {
          this.loadFromStore()
        }
        //  Si la source est de type 'service' (users, contacts), utiliser les données du backend
        else if (this.field.dataSource.type === 'service' && this.field.dataSource.data) {
          // Filtrer les options vides et formater (comme dans GenericFieldSelect)
          this.options = this.field.dataSource.data
            .filter(item => item.label && item.label.trim() !== '')
            .map(item => ({
              value: item.value,
              label: item.label || `User ${item.value}`
            }))
        }
        //  Sinon, utiliser les données déjà résolues côté backend via /config
        else if (this.field.dataSource.data && Array.isArray(this.field.dataSource.data)) {
          // Les données sont déjà résolues côté backend via /config
          this.options = this.field.dataSource.data.map(o => ({
            ...o,
            label: (o.label && String(o.label).startsWith('general.')) ? this.$t(o.label) : (o.label || o.libelle || o.name)
          }))
        } else {
          console.warn(`Pas de données dataSource.data pour le champ ${this.field.name}. Toutes les données doivent venir du /config.`)
          this.options = []
        }
      } catch (error) {
        console.error(`Erreur lors du chargement des options pour ${this.field.name}:`, error)
        this.options = []
        
        // Émettre l'erreur pour que le parent puisse la gérer
        this.$emit('error', {
          field: this.field.name,
          error: error.message
        })
      } finally {
        this.dataLoading = false
      }
    },

    async loadDependentOptions(dependencyValue) {
      if (!dependencyValue) {
        console.log(`[GenericFieldSelect ${this.field.name}] loadDependentOptions: no dependency value, clearing options`)
        this.options = []
        this.$emit('input', null)
        return
      }

      this.dataLoading = true
      
      try {
        const dependsOnField = this.getProperty('dependsOn')
        const requestPayload = {
          businessDomain: this.businessDomain,
          fieldName: this.field.name,
          dataSourceCode: this.field.dataSource.code,
          parameters: {
            [dependsOnField]: dependencyValue
          },
          userContext: this.userContext
        }
        
        console.log(`[GenericFieldSelect ${this.field.name}] Calling API with payload:`, requestPayload)
        
        const response = await searchFormApi.getFieldData(requestPayload)
        
        console.log(`[GenericFieldSelect ${this.field.name}] API response:`, response)

        if (response && response.data && Array.isArray(response.data)) {
          this.options = response.data.map(o => ({
            ...o,
            label: (o.label && String(o.label).startsWith('general.')) ? this.$t(o.label) : (o.label || o.libelle || o.name)
          }))
          console.log(`[GenericFieldSelect ${this.field.name}] Loaded ${this.options.length} options`)
        } else {
          this.options = []
          console.warn(`[GenericFieldSelect ${this.field.name}] No data in response or not an array`)
        }

        this.$emit('input', null)
      } catch (error) {
        console.error(`Erreur lors du chargement des options dépendantes pour ${this.field.name}:`, error)
        this.options = []
        this.$emit('error', {
          field: this.field.name,
          error: error.message
        })
      } finally {
        this.dataLoading = false
      }
    },

    loadFromStore() {
      try {
        
        // RÉCUPÉRATION DEPUIS LE STORE VUEX
        const storeData = this.getStoreData()
        
        if (Array.isArray(storeData)) {
          // Formater les données selon le champ
          this.options = this.formatStoreData(storeData)
        } else {
          this.options = []
        }
        
      } catch (error) {
        this.options = []
      }
    },
    
    getStoreData() {
      //  Les paramètres sont dans field.dataSource.parameters (venant du backend)
      const parameters = this.field.dataSource.parameters || {}
      const storePath = parameters.storePath || this.field.dataSource.storePath
      const storeGetter = parameters.storeGetter || this.field.dataSource.storeGetter
      
      
      // Utiliser le getter si disponible (ex: 'sitimmoUser/stocks')
      if (storeGetter && this.$store.getters[storeGetter]) {
        const data = this.$store.getters[storeGetter]
        return data
      }
      
      // Ou accéder directement au state avec un chemin (ex: 'sitimmoUser.stocks')
      if (storePath) {
        const parts = storePath.split('.')
        let data = this.$store.state
        for (const part of parts) {
          data = data && data[part]
        }
        return data
      }
      
      console.warn(` [getStoreData] Aucune source trouvée pour ${this.field.name}`)
      return []
    },
    
    formatStoreData(storeData) {
      //  Récupérer les champs configurés dans les paramètres du backend
      const parameters = this.field.dataSource.parameters || {}
      const valueField = parameters.valueField || 'value'
      const labelField = parameters.labelField || 'label'
      
      //  Format générique basé sur la configuration backend
      return storeData.map(item => {
        const rawLabel = item[labelField] || item.label || item.libelle || item.name || item.nom || item.value
        const option = {
          value: item[valueField] || item.value || item.code || item.id,
          label: this.capitalizeFirstLetter(rawLabel)
        }
        console.log(`  → Option:`, option)
        return option
      })
    },
    
    //  Fonction utilitaire pour capitaliser la première lettre
    capitalizeFirstLetter(str) {
      if (!str || typeof str !== 'string') return str
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
    },

    async handleRemoteSearch(query) {
      if (!query || query.length < 2) {
        this.remoteOptions = []
        return
      }

      if (query === this.lastRemoteQuery) {
        return
      }

      this.loading = true
      this.lastRemoteQuery = query

      try {
        //  Recherche locale dans les données du /config au lieu d'appels API
        if (this.field.dataSource.data && Array.isArray(this.field.dataSource.data)) {
          const searchLower = query.toLowerCase()
          this.remoteOptions = this.field.dataSource.data.filter(item => {
            const label = item.label || item.libelle || item.name || ''
            return label.toLowerCase().includes(searchLower)
          }).slice(0, this.getProperty('remoteLimit', 50))
        } else {
          console.warn(`Recherche remote impossible: pas de données dataSource.data pour ${this.field.name}`)
          this.remoteOptions = []
        }
      } catch (error) {
        console.error(`Erreur lors de la recherche remote pour ${this.field.name}:`, error)
        this.remoteOptions = []
      } finally {
        this.loading = false
      }
    },

    buildDataSourceParameters() {
      // Construction des paramètres dynamiques selon la configuration
      const parameters = {}
      
      // Ajouter les paramètres de la source de données
      if (this.field.dataSource && this.field.dataSource.parameters) {
        // dataSource.parameters est un OBJET, pas un Array
        if (Array.isArray(this.field.dataSource.parameters)) {
          // Format legacy (array)
          this.field.dataSource.parameters.forEach(param => {
            parameters[param.name] = this.resolveParameterValue(param.value)
          })
        } else {
          // Format API actuel (object)
          Object.entries(this.field.dataSource.parameters).forEach(([key, value]) => {
            parameters[key] = this.resolveParameterValue(value)
          })
        }
      }

      return parameters
    },

    resolveParameterValue(paramValue) {
      // Résolution des paramètres dynamiques
      if (typeof paramValue === 'string' && paramValue.startsWith('{{') && paramValue.endsWith('}}')) {
        //  NE PAS émettre d'événement ici - cela peut créer une boucle infinie
        // const paramName = paramValue.slice(2, -2)
        // Pour l'instant, retourner la valeur telle quelle
        return paramValue
      }
      
      return paramValue
    },

    handleInput(inputValue) {
      this.$emit('input', inputValue)
    },

    handleChange(selectedValue) {
      console.log(`[GenericFieldSelect ${this.field.name}] handleChange:`, selectedValue)
      this.$emit('input', selectedValue)
      this.$emit('change', { field: this.field.name, value: selectedValue })
      
      // Si on vide un champ qui a des dépendants, vider aussi les champs dépendants
      if (!selectedValue || selectedValue === '') {
        const hasDependents = this.getProperty('hasDependents')
        if (hasDependents && Array.isArray(hasDependents)) {
          console.log(`[GenericFieldSelect ${this.field.name}] Clearing dependent fields:`, hasDependents)
          hasDependents.forEach(dependentField => {
            this.$emit('change', { field: dependentField, value: '' })
          })
        }
      }
      
      // Pour les selects simples (non-multiples, non-cascaders), fermer la popup et déclencher la recherche
      if (!this.isMultiple && !this.isCascaderType) {
        console.log(`[GenericFieldSelect ${this.field.name}] Select simple: fermeture de la popup et recherche`)
        this.$emit('close-and-search')
      }
    },

    //  BACKUP : Garder visible-change au cas où ça fonctionne sur certains composants
    handleVisibleChange(visible) {
      // Quand le popup se ferme (visible = false), déclencher sauvegarde + recherche
      if (!visible) {
        this.$emit('popup-closed', {
          field: this.field.name,
          shouldTriggerSearch: true
        })
      }
    },

    getProperty(propertyName, defaultValue = undefined) {
      //  Valeurs par défaut pour les popups
      if (propertyName === 'filterable' && defaultValue === undefined) {
        defaultValue = true // Activer la recherche par défaut dans les popups
      }
      if (propertyName === 'clearable' && defaultValue === undefined) {
        defaultValue = true // Activer le clear par défaut dans les popups
      }
      
      //  PRIORITÉ 1: componentProps (format JSON direct depuis la DB)
      if (this.field.componentProps && this.field.componentProps[propertyName] !== undefined) {
        const value = this.field.componentProps[propertyName]
        return value
      }

      //  PRIORITÉ 2: properties (format objet direct depuis le backend)
      if (this.field.properties && typeof this.field.properties === 'object' && !Array.isArray(this.field.properties)) {
        if (this.field.properties[propertyName] !== undefined) {
          return this.field.properties[propertyName]
        }
      }

      //  PRIORITÉ 3: properties (format legacy avec array)
      if (this.field.properties && Array.isArray(this.field.properties)) {
        const property = this.field.properties.find(p => p.name === propertyName)
        if (property) {
          let finalValue
          switch (property.type) {
            case 'boolean':
              finalValue = property.value === 'true' || property.value === true
              break
            case 'number':
            case 'integer':
              finalValue = property.value ? Number(property.value) : defaultValue
              break
            case 'string':
            default:
              finalValue = property.value || defaultValue
              break
          }
          
          return finalValue
        }
      }
      
      //  PRIORITÉ 4: valeur par défaut
      return defaultValue
    },

    // Traduire un label s'il s'agit d'une clé i18n
    translateLabel(label) {
      if (!label) return ''
      
      // Si le label contient un point, c'est probablement une clé i18n (ex: "planning.all")
      if (typeof label === 'string' && label.includes('.')) {
        // Essayer de traduire avec $t
        const translated = this.$t(label)
        // Si la traduction retourne la même clé, c'est que la traduction n'existe pas
        if (translated !== label) {
          return translated
        }
      }
      
      // Sinon, retourner le label tel quel
      return label
    }
  }
}
</script>

<style scoped>
.search-field {
  margin-bottom: 16px;
}

.search-field-required .el-form-item__label::after {
  content: ' *';
  color: #f56c6c;
}

.search-field-disabled {
  opacity: 0.6;
}

.field-help-text {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.4;
}

.field-loading {
  margin-top: 4px;
  font-size: 12px;
}

.text-muted {
  color: #909399;
}

.text-info {
  color: #409eff;
}

.option-with-description {
  line-height: 1.3;
}

/* Styles pour le select multiple */
.search-field-multiple .el-select .el-tag {
  margin-right: 6px;
  margin-bottom: 2px;
}

.search-field-multiple .el-select .el-tag .el-tag__close {
  font-size: 12px;
}

/* Styles pour le select avec tags collapsés */
.el-select .el-tag--info {
  background-color: #f4f4f5;
  border-color: #e9e9eb;
  color: #909399;
}
</style>
